/**
 * Hand-drawn luxury silhouette icons for product thumbnails.
 * Monochrome; inherit color from parent via currentColor.
 * Lighter than any image asset, crisp at any size.
 */

type IconProps = { className?: string; size?: number };

export function HandbagIcon({ className = '', size = 48 }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Handle */}
      <path d="M15 18 C 15 11, 21 8, 24 8 C 27 8, 33 11, 33 18" />
      {/* Body */}
      <path d="M10 18 L 38 18 L 36 40 L 12 40 Z" fill="currentColor" fillOpacity="0.08" />
      {/* Clasp */}
      <rect x="22" y="22" width="4" height="3" rx="0.5" fill="currentColor" fillOpacity="0.3" />
      {/* Stitching hint */}
      <path d="M 12 20 L 36 20" strokeDasharray="1 1.5" strokeWidth="0.5" opacity="0.6" />
    </svg>
  );
}

export function WatchIcon({ className = '', size = 48 }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Strap top */}
      <path d="M 19 6 L 19 14 L 29 14 L 29 6" fill="currentColor" fillOpacity="0.08" />
      {/* Strap bottom */}
      <path d="M 19 34 L 19 42 L 29 42 L 29 34" fill="currentColor" fillOpacity="0.08" />
      {/* Case */}
      <circle cx="24" cy="24" r="10" fill="currentColor" fillOpacity="0.1" />
      <circle cx="24" cy="24" r="10" />
      {/* Crown */}
      <path d="M 34 22 L 36 22 L 36 26 L 34 26" />
      {/* Hands */}
      <line x1="24" y1="24" x2="24" y2="18" strokeWidth="0.9" />
      <line x1="24" y1="24" x2="28" y2="25.5" strokeWidth="0.9" />
      {/* Dial markers */}
      <circle cx="24" cy="15" r="0.6" fill="currentColor" />
      <circle cx="33" cy="24" r="0.6" fill="currentColor" />
      <circle cx="24" cy="33" r="0.6" fill="currentColor" />
      <circle cx="15" cy="24" r="0.6" fill="currentColor" />
    </svg>
  );
}

export function WalletIcon({ className = '', size = 48 }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Body */}
      <rect x="8" y="14" width="32" height="22" rx="2" fill="currentColor" fillOpacity="0.08" />
      {/* Flap */}
      <path d="M 8 14 L 26 8 L 40 14" />
      {/* Clasp circle */}
      <circle cx="32" cy="25" r="2.5" fill="currentColor" fillOpacity="0.2" />
      <circle cx="32" cy="25" r="1" fill="currentColor" />
      {/* Stitching */}
      <path d="M 10 18 L 38 18" strokeDasharray="1 1" strokeWidth="0.5" opacity="0.5" />
    </svg>
  );
}

export function JewelryIcon({ className = '', size = 48 }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Diamond/gem */}
      <path d="M 24 10 L 32 20 L 24 38 L 16 20 Z" fill="currentColor" fillOpacity="0.1" />
      <path d="M 16 20 L 32 20" />
      <path d="M 24 10 L 24 20" strokeWidth="0.8" />
      <path d="M 20 20 L 24 38" strokeWidth="0.6" />
      <path d="M 28 20 L 24 38" strokeWidth="0.6" />
      {/* Shine */}
      <circle cx="20" cy="14" r="0.8" fill="currentColor" />
    </svg>
  );
}

export function SunglassesIcon({ className = '', size = 48 }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <ellipse cx="14" cy="24" rx="7" ry="6" fill="currentColor" fillOpacity="0.12" />
      <ellipse cx="34" cy="24" rx="7" ry="6" fill="currentColor" fillOpacity="0.12" />
      <path d="M 21 23 C 22 21, 26 21, 27 23" />
      <path d="M 4 20 L 7 21" />
      <path d="M 41 21 L 44 20" />
    </svg>
  );
}

export function CameraIcon({ className = '', size = 48 }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M 8 16 L 14 16 L 17 12 L 31 12 L 34 16 L 40 16 C 41 16, 42 17, 42 18 L 42 36 C 42 37, 41 38, 40 38 L 8 38 C 7 38, 6 37, 6 36 L 6 18 C 6 17, 7 16, 8 16 Z" />
      <circle cx="24" cy="26" r="7" fill="currentColor" fillOpacity="0.1" />
      <circle cx="24" cy="26" r="7" />
      <circle cx="24" cy="26" r="3.5" fill="currentColor" fillOpacity="0.3" />
      <circle cx="36" cy="20" r="1" fill="currentColor" />
    </svg>
  );
}

export function BellIcon({ className = '', size = 48 }: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M 24 8 C 17 8, 13 13, 13 20 L 13 28 L 9 34 L 39 34 L 35 28 L 35 20 C 35 13, 31 8, 24 8 Z" fill="currentColor" fillOpacity="0.1" />
      <path d="M 20 36 C 20 39, 22 40, 24 40 C 26 40, 28 39, 28 36" />
      <circle cx="24" cy="6" r="1.2" fill="currentColor" />
    </svg>
  );
}

/** Returns the right icon component for a category string. */
export type IconName = 'handbag' | 'watch' | 'wallet' | 'jewelry' | 'sunglasses';
export function LuxuryIcon({ kind, ...rest }: { kind: IconName } & IconProps) {
  const Icon =
    kind === 'watch'
      ? WatchIcon
      : kind === 'wallet'
      ? WalletIcon
      : kind === 'jewelry'
      ? JewelryIcon
      : kind === 'sunglasses'
      ? SunglassesIcon
      : HandbagIcon;
  return <Icon {...rest} />;
}
